import React, { useState, useEffect, useRef, useCallback } from "react";
import "./App.css";
import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import {
  Menu,
  X,
  ArrowRight,
  ChevronRight,
  Newspaper,
  Info,
  Phone,
} from "lucide-react";

const Logo = "/images/olympic-wave-logo.png";
const LogoNoText = "/images/olympic-wave-logo-no-text.png";
const Photo1 =
  "/images/aidan-li-and-wajdi-bouallegue-2026-artistic-gymnastics-cottus-world-cup.jpg";
const Photo2 =
  "/images/aidan-li-and-wajdi-bouallegue-2025-artistic-gymnastics-world-championships-2.png";
const Photo3 =
  "/images/aidan-li-and-wajdi-bouallegue-2025-artistic-gymnastics-world-championships-3.png";
const Photo4 =
  "/images/aidan-li-and-wajdi-bouallegue-2025-artistic-gymnastics-world-championships.png";
const Photo5 =
  "/images/aidan-li-and-wajdi-bouallegue-2026-artistic-gymnastics-cottus-world-cup-3.jpg";
const Photo6 =
  "/images/aidan-li-and-wajdi-bouallegue-2026-artistic-gymnastics-cottus-world-cup-2.jpg";

/* ─────────────────────────────────────────
   WAVE BACKGROUND
───────────────────────────────────────── */
export function WaveCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf,
      tick = 0;
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      canvas.style.width = "100%";
      canvas.style.height = "100%";
    };
    resize();
    window.addEventListener("resize", resize);
    const WAVES = [
      { amp: 34, freq: 0.0065, speed: 0.013, yFrac: 0.7, alpha: 0.12 },
      { amp: 22, freq: 0.011, speed: 0.019, yFrac: 0.78, alpha: 0.2 },
      { amp: 46, freq: 0.005, speed: 0.008, yFrac: 0.62, alpha: 0.07 },
    ];
    const draw = () => {
      const { width, height } = canvas;

      ctx.fillStyle = "#080b11";
      ctx.fillRect(0, 0, width, height);

      WAVES.forEach(({ amp, freq, speed, yFrac, alpha }) => {
        ctx.beginPath();
        ctx.moveTo(0, height);
        for (let x = 0; x <= width; x += 4) {
          ctx.lineTo(
            x,
            height * yFrac + Math.sin(x * freq + tick * speed) * amp,
          );
        }
        ctx.lineTo(width, height);
        ctx.closePath();
        ctx.fillStyle = `rgba(59,130,246,${alpha})`;
        ctx.fill();
      });

      const fadeGradient = ctx.createLinearGradient(
        0,
        height * 0.55,
        0,
        height,
      );
      fadeGradient.addColorStop(0, "rgba(8,11,17,0)");
      fadeGradient.addColorStop(1, "rgba(8,11,17,1)");

      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = fadeGradient;
      ctx.fillRect(0, 0, width, height);
      ctx.globalCompositeOperation = "source-over";

      tick++;
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return <canvas ref={ref} className="ow-wave-canvas" />;
}

/* ─────────────────────────────────────────
   CAROUSEL
───────────────────────────────────────── */

function useBreakpoint() {
  const get = () => {
    if (window.innerWidth > 900) return "desktop";
    if (window.matchMedia("(orientation: landscape)").matches)
      return "landscape";
    return "mobile";
  };
  const [bp, setBp] = useState(get);
  useEffect(() => {
    const handler = () => setBp(get());
    window.addEventListener("resize", handler);
    window.addEventListener("orientationchange", handler);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("orientationchange", handler);
    };
  }, []);
  return bp;
}
function PhotoGrid() {
  const [order, setOrder] = useState(SLIDES.map((_, i) => i));
  const [fadingOut, setFadingOut] = useState([]);
  const [fadingIn, setFadingIn] = useState([]);
  const busyPositions = useRef(new Set());

  useEffect(() => {
    const timeouts = [];

    const runSwap = (a, b) => {
      busyPositions.current.add(a);
      busyPositions.current.add(b);

      setFadingOut((prev) => [...prev, a, b]);

      const t2 = setTimeout(() => {
        setOrder((prev) => {
          const next = [...prev];
          [next[a], next[b]] = [next[b], next[a]];
          return next;
        });
        setFadingOut((prev) => prev.filter((x) => x !== a && x !== b));
        setFadingIn((prev) => [...prev, a, b]);

        const t3 = setTimeout(() => {
          setFadingIn((prev) => prev.filter((x) => x !== a && x !== b));
          busyPositions.current.delete(a);
          busyPositions.current.delete(b);
        }, 100);

        timeouts.push(t3);
      }, 2500);

      timeouts.push(t2);
    };

    const doSwap = () => {
      const positions = Array.from({ length: SLIDES.length }, (_, i) => i);
      for (let i = positions.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [positions[i], positions[j]] = [positions[j], positions[i]];
      }

      const numPairs = Math.floor(Math.random() * 3) + 1;
      const pairs = Array.from({ length: numPairs }, (_, i) => [
        positions[i * 2],
        positions[i * 2 + 1],
      ]);

      let startedThisRound = 0;
      pairs.forEach(([a, b]) => {
        const busy = busyPositions.current;
        if (
          !busy.has(a) &&
          !busy.has(b) &&
          busy.size + 2 <= 6 &&
          startedThisRound < numPairs
        ) {
          runSwap(a, b);
          startedThisRound++;
        }
      });

      const nextDelay = 3000 + Math.random() * 3000;
      const t = setTimeout(doSwap, nextDelay + 2600);
      timeouts.push(t);
    };

    const init = setTimeout(doSwap, 1500);
    timeouts.push(init);

    return () => timeouts.forEach(clearTimeout);
  }, []);

  return (
    <div className="ow-photo-grid">
      {order.map((slideIdx, gridPos) => {
        const slide = SLIDES[slideIdx];
        const isFadingOut = fadingOut.includes(gridPos);
        const isFadingIn = fadingIn.includes(gridPos);
        return (
          <div
            key={slideIdx}
            className={`ow-photo-grid-item ow-photo-grid-item--${gridPos}`}
            style={{
              opacity: isFadingOut || isFadingIn ? 0 : 1,
              transition: "opacity 2.5s ease, transform 0.6s ease",
            }}
          >
            <img
              src={slide.src}
              alt={slide.alt}
              style={{ objectPosition: slide.posDesktop }}
            />
          </div>
        );
      })}
    </div>
  );
}
const SLIDES = [
  {
    src: Photo1,
    alt: "Aidan Li and coach Wajdi Bouallègue at the 2026 Cottbus Artistic Gymnastics World Cup",
    pos: "center top",
    posLandscape: "center 40%",
    posDesktop: "center 30%",
  },
  {
    src: Photo2,
    alt: "Aidan Li and Wajdi Bouallègue celebrating at the 2025 Artistic Gymnastics World Championships",
    pos: "60% center",
    posLandscape: "60% 60%",
    posDesktop: "center 47%",
  },
  {
    src: Photo3,
    alt: "Aidan Li and Wajdi Bouallègue together at the 2025 World Championships",
    pos: "center top",
    posLandscape: "center 50%",
    posDesktop: "center 40%",
  },
  {
    src: Photo4,
    alt: "Aidan Li and coach Wajdi Bouallègue at the 2025 Artistic Gymnastics World Championships",
    pos: "center",
    posLandscape: "center",
    posDesktop: "center 40%",
  },
  {
    src: Photo5,
    alt: "Aidan Li and Wajdi Bouallègue at the 2026 Cottbus World Cup podium",
    pos: "center 20%",
    posLandscape: "center 30%",
    posDesktop: "center 30%",
  },
  {
    src: Photo6,
    alt: "Aidan Li and Wajdi Bouallègue at the 2026 Cottbus Artistic Gymnastics World Cup",
    pos: "center top",
    posLandscape: "center top",
    posDesktop: "center 20%",
  },
];

function Carousel() {
  const bp = useBreakpoint();
  const [cur, setCur] = useState(0);
  const [fading, setFading] = useState(false);
  const fadingRef = useRef(false);

  const FADE = bp === "desktop" ? 600 : 1200;

  const goTo = useCallback(
    (i) => {
      if (i === cur || fadingRef.current) return;
      fadingRef.current = true;
      setFading(true);
      setTimeout(() => {
        setCur(i);
        setFading(false);
        fadingRef.current = false;
      }, FADE);
    },
    [cur, FADE],
  );

  useEffect(() => {
    const iv = setInterval(() => goTo((cur + 1) % SLIDES.length), 4000 + FADE);
    return () => clearInterval(iv);
  }, [cur, goTo, FADE]);

  if (bp === "desktop") return <PhotoGrid />;

  const getPos = (slide) => {
    if (bp === "landscape") return slide.posLandscape;
    return slide.pos;
  };

  return (
    <div className="ow-carousel">
      <img
        src={SLIDES[cur].src}
        alt={SLIDES[cur].alt}
        className={`ow-carousel-img${fading ? " fading" : ""}`}
        style={{
          objectPosition: getPos(SLIDES[cur]),
          transition: `opacity ${FADE}ms ease`,
        }}
      />
      <div className="ow-carousel-overlay" />
      <div className="ow-carousel-dots">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            className={`ow-dot${i === cur ? " active" : ""}`}
            onClick={() => goTo(i)}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   SHARED NAV  (exported for other pages)
───────────────────────────────────────── */
export function SiteNav() {
  const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const saved = localStorage.getItem("ow-lang");
    if (saved && saved !== i18n.language) {
      i18n.changeLanguage(saved);
    }
  }, [i18n]);

  const toggleLang = () => {
    const next = i18n.language === "en" ? "fr" : "en";
    i18n.changeLanguage(next);
    localStorage.setItem("ow-lang", next);
  };
  const langLabel = i18n.language === "en" ? "FR" : "EN";

  const links = [
    { to: "/", label: t("home"), end: true },
    { to: "/services", label: "Services" },
    { to: "/news", label: t("news") },
    { to: "/about", label: t("about") },
    { to: "/contact", label: t("contact") },
  ];

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, { passive: true });
    return () => window.removeEventListener("scroll", close);
  }, [open]);

  return (
    <>
      <header className={`ow-header${open ? " ow-header--open" : ""}`}>
        <NavLink to="/" className="ow-logo" onClick={() => setOpen(false)}>
          Olympic <span className="ow-logo-accent">Wave</span>
        </NavLink>

        <nav className="ow-nav-desktop">
          {links.map(({ to, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `ow-nav-link${isActive ? " active" : ""}`
              }
            >
              {label}
            </NavLink>
          ))}
          <button className="ow-lang-pill" onClick={toggleLang}>
            {langLabel}
          </button>
        </nav>

        <button
          className="ow-burger"
          onClick={() => setOpen(!open)}
          aria-label="menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
        <div className={`ow-mobile-drawer${open ? " open" : ""}`}>
          {links.map(({ to, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `ow-mobile-link${isActive ? " active" : ""}`
              }
              onClick={() => setOpen(false)}
            >
              {label}
            </NavLink>
          ))}
          <button className="ow-mobile-lang" onClick={toggleLang}>
            {langLabel}
          </button>
        </div>
      </header>
    </>
  );
}

/* ─────────────────────────────────────────
   SHARED FOOTER  (exported for other pages)
───────────────────────────────────────── */
export function SiteFooter() {
  const { t } = useTranslation();
  return (
    <footer className="ow-footer">
      <div className="ow-footer-inner">
        <div className="ow-footer-brand-col">
          <Link to="/">
            <img
              src={LogoNoText}
              alt="Olympic Wave logo"
              className="footer-logo"
            />
          </Link>
          <p className="ow-footer-tagline">{t("quote")}</p>
        </div>
        <div>
          <p className="ow-footer-heading">{t("links")}</p>
          <ul className="ow-footer-list">
            {[
              ["/", t("home")],
              ["/services", t("service")],
              ["/news", t("news")],
              ["/about", t("about")],
              ["/contact", t("contact")],
            ].map(([h, l]) => (
              <li key={h}>
                <a href={h}>{l}</a>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="ow-footer-heading">{t("getintouch")}</p>
          <ul className="ow-footer-list">
            <li>
              <a href="tel:+13439870303">(343) 987-0303</a>
            </li>
            <li>
              <a href="mailto:Wajdi@olympicwave.ca">Wajdi@olympicwave.ca</a>
            </li>
          </ul>
        </div>
      </div>
      <div className="ow-footer-bottom">
        © {new Date().getFullYear()} {t("nom")}. {t("rights")}.
      </div>
    </footer>
  );
}

/* ─────────────────────────────────────────
   HOME PAGE
───────────────────────────────────────── */
export default function Home() {
  const [popup, setPopup] = useState(false);
  const { t } = useTranslation();

  const cards = [
    { to: "/services", Icon: ChevronRight, label: t("messervices") },
    { to: "/news", Icon: Newspaper, label: t("news") },
    { to: "/about", Icon: Info, label: t("learnmore") },
    { to: "/contact", Icon: Phone, label: t("contactme") },
  ];

  return (
    <>
      <Helmet>
        <title>Olympic Wave</title>
        <meta
          name="description"
          content="Official platform of Olympic gymnast Wajdi Bouallègue. Olympic Wave offers elite gymnastics coaching, athlete development, and highlights competition achievements of his athletes."
        />
        <meta
          property="og:title"
          content="Olympic Wave | Elite Gymnastics Coaching by Wajdi Bouallègue"
        />
        <meta
          property="og:description"
          content="Official platform of Olympic gymnast Wajdi Bouallègue."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://olympicwave.ca/" />
        <meta
          property="og:image"
          content="https://olympicwave.ca/olympic-wave-logo-1.png"
        />
        <link rel="canonical" href="https://olympicwave.ca/" />
      </Helmet>

      <WaveCanvas />

      <div className="ow-page">
        {popup && (
          <div className="ow-popup">
            <p>{t("newsPopupText")}</p>
            <Link to="/news" className="ow-popup-link">
              {t("readNews")}
            </Link>
            <button
              className="ow-popup-close"
              onClick={() => setPopup(false)}
              aria-label="close"
            >
              <X size={14} />
            </button>
          </div>
        )}

        <SiteNav />
        <div className="ow-inner-home">
          {/* HERO CAROUSEL */}
          <div className="ow-home-hero-img">
            <Carousel />
          </div>

          {/* HERO BELOW ROW */}
          <section className="ow-hero-below-row">
            <div className="ow-hero-below-text">
              <h1 className="ow-hero-title">
                {t("welcome")}
                <span className="ow-hero-title-brand">
                  {" "}
                  Olympic <span className="ow-logo-accent">Wave</span>
                </span>
              </h1>
              <p className="ow-hero-sub">{t("double")}</p>
              <div className="ow-hero-actions">
                <Link to="/services" className="ow-btn ow-btn-primary">
                  {t("messervices")} <ArrowRight size={15} strokeWidth={2.5} />
                </Link>
                <Link to="/contact" className="ow-btn ow-btn-ghost">
                  {t("contactme")}
                </Link>
              </div>
            </div>
            <div className="ow-hero-below-logo">
              <img
                src={Logo}
                alt="Olympic Wave logo"
                className="ow-home-hero-logo"
              />
            </div>
          </section>

          {/* ABOUT BLURB */}
          <div className="ow-home-blurb">
            <p className="ow-home-blurb-text">{t("je")}</p>
          </div>

          {/* CTA */}
          <div className="ow-home-cta">
            <h2 className="ow-cta-title">{t("plus")}</h2>
            <p className="ow-cta-desc">{t("btn-des")}</p>
            <div className="ow-card-grid">
              {cards.map(({ to, Icon, label }) => (
                <Link key={to} to={to} className="ow-nav-card">
                  <span className="ow-nav-card-icon">
                    <Icon size={18} strokeWidth={2} />
                  </span>
                  <span>{label}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
        <SiteFooter />
      </div>
    </>
  );
}
